// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a pin's history across the dataset's own time window.
 * Ported from AYNI 2D (`src/shared/measurement-series.ts`) for the Data tab
 * (docs/AYNI_TOOLS_DATA_PLAN.md, Phase 3).
 *
 * Keyed by where a frame sits in the window, never by the order samples
 * arrived. That distinction is the whole design:
 *
 *  - These streams loop. A pin placed 60% of the way through fills its
 *    remaining slots as playback comes round again, so the graph *develops*
 *    rather than restarting. An append-only buffer would put the window's
 *    first frame after its last one on the wrap and draw a sawtooth that
 *    looks like data.
 *  - The axis is stable from the moment a pin is placed: the dataset's
 *    window, not "time since I clicked". Holes fill in; the axis never moves
 *    under the reader.
 *  - Scrubbing fills slots too, so a teacher can complete a graph
 *    deliberately instead of waiting a lap.
 *  - AYNI (2026-10-10): one slot per frame when the stream says how many
 *    (up to {@link MAX_SERIES_SLOTS}). With fewer slots than frames, frames
 *    shared a slot and each lap overwrote it with whichever neighbour the
 *    200 ms sampler hit, so the line changed shape from lap to lap. Now a
 *    frame read on any lap is kept and each lap only adds frames; the
 *    graph and the statistics thin it ({@link thinSeries}) instead.
 *
 * Two kinds of empty, which must never look alike. A slot nobody has visited
 * yet will fill in; a slot the instrument saw nothing in never will. Drawn
 * the same, a viewer cannot tell whether waiting would help, and on
 * cloud-masked or land-masked products most frames have both.
 */

/** One frame's worth of a series. */
export interface SeriesSlot {
  /** True once a frame landed here, whatever it contained. */
  sampled: boolean
  /** The value read, or null for a frame the instrument had no data in. */
  value: number | null
  /**
   * AYNI: the time of the frame read into this slot (UTC ms, or playback ms).
   * A slot can hold any of several frames when there are more frames than
   * slots; a climatology needs the calendar month of the one it holds.
   */
  timeMs?: number
  /**
   * AYNI (owner, 2026-10-18): where the value was read, for a pin that
   * follows the highest or lowest value (`followExtremes.ts`): each frame
   * at another place, which its track draws. Absent for a pin that stays.
   */
  at?: { lat: number; lon: number }
}

export interface MeasurementSeries {
  /** The window's first and last frame: UTC ms, or playback ms for a dataset without dates. */
  startMs: number
  endMs: number
  slots: SeriesSlot[]
}

/** Slots per window when the number of frames is not known. Enough to draw against, small enough to hold many of. */
export const DEFAULT_SERIES_SLOTS = 256

/**
 * AYNI: the most slots a series takes, one per frame up to here. The
 * longest stream had 6,693 frames on 2026-10-10; past this, frames share
 * a slot again (the last read is kept).
 */
export const MAX_SERIES_SLOTS = 20_000

export function createSeries(
  startMs: number,
  endMs: number,
  slotCount: number = DEFAULT_SERIES_SLOTS,
): MeasurementSeries | null {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null
  const count = Math.max(2, Math.floor(slotCount))
  return {
    startMs,
    endMs,
    slots: Array.from({ length: count }, () => ({ sampled: false, value: null })),
  }
}

/** Which slot a frame time belongs to, or -1 when it falls outside the window. */
export function slotForTime(series: MeasurementSeries, frameTimeMs: number): number {
  if (!Number.isFinite(frameTimeMs)) return -1
  const span = series.endMs - series.startMs
  if (span <= 0) return -1
  const fraction = (frameTimeMs - series.startMs) / span
  if (fraction < 0 || fraction > 1) return -1
  return Math.min(series.slots.length - 1, Math.round(fraction * (series.slots.length - 1)))
}

/**
 * Record what a frame held. Re-visiting a slot overwrites it: the newest read
 * of a frame is as good as the first, and on a mutable tail it may be better.
 * AYNI: with a slot per frame that is the same frame again, never a neighbour.
 * `at`: where it was read, for a pin that follows an extreme.
 */
export function recordSample(series: MeasurementSeries, frameTimeMs: number, value: number | null, at?: { lat: number; lon: number }): boolean {
  const slot = slotForTime(series, frameTimeMs)
  if (slot < 0) return false
  series.slots[slot] = at ? { sampled: true, value, timeMs: frameTimeMs, at } : { sampled: true, value, timeMs: frameTimeMs }
  return true
}

/**
 * Grow the window without losing what is already recorded.
 *
 * Mutable-tail streams gain frames at the live edge, so the window a series
 * was created against stops being the whole story. Existing samples keep
 * their time rather than their slot index, because the slot a frame maps to
 * changes when the window does.
 *
 * AYNI: `slotCount` follows the frames (one slot per frame), so a window
 * that gains frames gains slots; the same window with a new slot count is
 * re-gridded the same way.
 */
export function extendSeries(series: MeasurementSeries, endMs: number, slotCount: number = series.slots.length): MeasurementSeries {
  const count = Math.max(2, Math.floor(slotCount))
  if (!Number.isFinite(endMs) || endMs < series.endMs) return series
  if (endMs === series.endMs && count === series.slots.length) return series
  const grown = createSeries(series.startMs, endMs, count)
  if (!grown) return series
  const span = series.endMs - series.startMs
  const lastIndex = series.slots.length - 1
  for (let i = 0; i <= lastIndex; i++) {
    const slot = series.slots[i]
    if (!slot.sampled) continue
    recordSample(grown, slot.timeMs ?? series.startMs + (span * i) / lastIndex, slot.value, slot.at)
  }
  return grown
}

/**
 * AYNI: the series on at most `maxSlots` slots, for drawing it a pixel
 * column apart or for statistics that cost the square of their points.
 * A thinned slot holds the slots that map to it by the series' own rule
 * (as {@link slotForTime} would place their frames): read when any was,
 * its value the mean of those read with data, no data only when every
 * one read had none, and its time the mean of theirs. The same series is
 * returned when it already fits. Every lap adds frames to a mean, so a
 * thinned line settles rather than changing.
 */
export function thinSeries(series: MeasurementSeries, maxSlots: number): MeasurementSeries {
  const n = series.slots.length
  const m = Math.max(2, Math.floor(maxSlots))
  if (n <= m) return series
  const sum = new Float64Array(m)
  const withData = new Uint32Array(m)
  const read = new Uint32Array(m)
  const timeSum = new Float64Array(m)
  const span = series.endMs - series.startMs
  for (let i = 0; i < n; i++) {
    const slot = series.slots[i]
    if (!slot.sampled) continue
    const b = Math.round((i * (m - 1)) / (n - 1))
    read[b]++
    timeSum[b] += slot.timeMs ?? series.startMs + (span * i) / (n - 1)
    if (slot.value == null) continue
    sum[b] += slot.value
    withData[b]++
  }
  const slots: SeriesSlot[] = Array.from({ length: m }, (_, b) => (!read[b]
    ? { sampled: false, value: null }
    : { sampled: true, value: withData[b] ? sum[b] / withData[b] : null, timeMs: timeSum[b] / read[b] }))
  return { startMs: series.startMs, endMs: series.endMs, slots }
}

export interface SeriesCoverage {
  /** Slots visited, whether or not they held data. */
  sampled: number
  /** Slots visited that held a value. */
  withData: number
  total: number
}

export function seriesCoverage(series: MeasurementSeries): SeriesCoverage {
  let sampled = 0
  let withData = 0
  for (const slot of series.slots) {
    if (!slot.sampled) continue
    sampled++
    if (slot.value != null) withData++
  }
  return { sampled, withData, total: series.slots.length }
}

export interface SeriesSummary {
  min: number | null
  max: number | null
  mean: number | null
  /** Latest recorded value, and where in the window it sits. */
  latest: number | null
  latestMs: number | null
  coverage: SeriesCoverage
}

/**
 * Statistics over what has actually been seen. Unsampled slots are absent,
 * not zero, and the coverage travels with the numbers so a caller can say
 * how much of the window the answer rests on.
 */
export function summarizeSeries(series: MeasurementSeries): SeriesSummary {
  const coverage = seriesCoverage(series)
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let count = 0
  let latest: number | null = null
  let latestMs: number | null = null
  const span = series.endMs - series.startMs
  const lastIndex = series.slots.length - 1
  for (let i = 0; i <= lastIndex; i++) {
    const slot = series.slots[i]
    if (!slot.sampled || slot.value == null) continue
    const value = slot.value
    if (value < min) min = value
    if (value > max) max = value
    sum += value
    count++
    latest = value
    latestMs = series.startMs + (span * i) / lastIndex
  }
  return {
    min: count ? min : null,
    max: count ? max : null,
    mean: count ? sum / count : null,
    latest,
    latestMs,
    coverage,
  }
}
